/**
 * lib/responsive.ts · single source of truth for responsive behavior
 *
 * Ported from sk-catalog-frontend so SK Queue shares the platform's responsive
 * conventions. Every screen derives its breakpoint state, padding and content
 * width from here — do NOT recompute `width < N` or read useWindowDimensions
 * for layout decisions ad-hoc elsewhere; import from this file instead.
 *
 * API:
 *   BP        — breakpoint edges (mobile / tablet / desktop)
 *   CONTENT   — max content widths (form / narrow / default / wide)
 *   useResponsive() → { width, height, device, isMobile, isTablet, isDesktop,
 *                       pad, contentPad, select() }
 */
import { Platform, useWindowDimensions, Dimensions } from 'react-native';

/** Breakpoint edges (px). `mobile` = sidebar→drawer + stacked rows. */
export const BP = { mobile: 820, tablet: 1024, desktop: 1280 } as const;

/** Max content widths — centralized so screens stop hardcoding 680/760/1100/1240. */
export const CONTENT = {
  form: 560,    // single-column forms (Operator, Config)
  narrow: 720,  // focused reading
  default: 1000,// standard
  wide: 1280,   // dense data surfaces (Host, Live View, Voice, Chat boards)
} as const;

export const isWeb = Platform.OS === 'web';

export type DeviceClass = 'mobile' | 'tablet' | 'desktop';

export interface SelectMap<T> { mobile: T; tablet?: T; desktop?: T }

function classify(width: number): DeviceClass {
  if (width < BP.mobile) return 'mobile';
  if (width < BP.tablet) return 'tablet';
  return 'desktop';
}

function pick<T>(device: DeviceClass, map: SelectMap<T>): T {
  if (device === 'desktop') return map.desktop ?? map.tablet ?? map.mobile;
  if (device === 'tablet') return map.tablet ?? map.mobile;
  return map.mobile;
}

export interface Responsive {
  width: number;
  height: number;
  device: DeviceClass;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isWeb: boolean;
  /** Standard horizontal screen padding for the current device. */
  pad: number;
  select<T>(map: SelectMap<T>): T;
}

function build(width: number, height: number): Responsive {
  const device = classify(width);
  return {
    width, height, device,
    isMobile: device === 'mobile',
    isTablet: device === 'tablet',
    isDesktop: device === 'desktop',
    isWeb,
    pad: device === 'mobile' ? 16 : 28,
    select: (map) => pick(device, map),
  };
}

/** Reactive responsive state — re-renders on viewport changes. */
export function useResponsive(): Responsive {
  const { width, height } = useWindowDimensions();
  return build(width, height);
}

/** Non-reactive snapshot for code outside React render. */
export function getViewport(): Responsive {
  const { width, height } = Dimensions.get('window');
  return build(width, height);
}
